import { useMemo, useState, useEffect } from 'react';
import { groupTasksByStatus } from '../../FlowchartView/utils/statusUtils';
import { useCampaignProgressStats } from '../../shared/CampaignHeader/hooks/useCampaignProgressStats';
import { getOrderedColumns, getStoredCollapsedColumns } from '../utils';
import { groupTasksIntoSwimlanes, DEFAULT_SWIMLANE_SETTING } from '../../config/swimlaneConfig';
import { sortTasks } from '../../components/SortDropdown';
import { calculateLockStatesWithParentContext } from '../../FlowchartView/utils/dependencyUtils';
import { useWaitGateNow } from '../../shared/ConditionsDropdown/utils/useWaitGateNow';
import { filterActionableTasks } from '../../shared/utils/taskAvailabilityUtils';
import { applyFilters } from '../../shared/filterUtils';

/**
 * useKanbanData Hook
 * 
 * Manages all derived/memoized data for the Kanban view:
 * - Ordered columns
 * - Tasks grouped by status
 * - Task IDs by column
 * - Completion stats
 * - Swimlane grouping
 * - Collapsed column state
 */
const useKanbanData = ({
  tasks,
  statuses,
  kanbanColumnOrder,
  campaignId,
  allTags,
  nestedSprints,
  swimlaneSetting = DEFAULT_SWIMLANE_SETTING,
  sortSetting = 'manual',
  filters = {},
  showArchived = false,
  showActionableOnly = false,
  customFields = [],
  localSprint = null,
  allSprints = [],
}) => {
  // Calculate lock states for mission type filtering and actionable filtering
  const hasMissionTypeFilter = filters?.missionType?.length > 0;
  const waitNow = useWaitGateNow(tasks);
  const lockStates = useMemo(() => {
    if (!showActionableOnly && !hasMissionTypeFilter) return {};
    return calculateLockStatesWithParentContext(tasks, nestedSprints, statuses, {
      currentSprint: localSprint,
      allSprints,
      now: waitNow,
    });
  }, [tasks, nestedSprints, statuses, showActionableOnly, hasMissionTypeFilter, localSprint, allSprints, waitNow]);

  // First apply standard filters (including custom fields)
  const baseFilteredTasks = useMemo(() => {
    return applyFilters(tasks, filters, showArchived, lockStates, nestedSprints, statuses, false, customFields);
  }, [tasks, filters, showArchived, lockStates, nestedSprints, statuses, customFields]);

  // Apply actionable filter if enabled
  const filteredTasks = useMemo(() => {
    if (!showActionableOnly) return baseFilteredTasks;
    return filterActionableTasks(baseFilteredTasks, lockStates, nestedSprints, statuses);
  }, [baseFilteredTasks, showActionableOnly, lockStates, nestedSprints, statuses]);

  // Collapsed columns state
  const [collapsedColumns, setCollapsedColumns] = useState(() => getStoredCollapsedColumns(campaignId));
  const [expandingColumns, setExpandingColumns] = useState(new Set());

  useEffect(() => {
    setCollapsedColumns(getStoredCollapsedColumns(campaignId));
  }, [campaignId]);

  // Column data
  const columns = useMemo(() => getOrderedColumns(statuses, kanbanColumnOrder), [statuses, kanbanColumnOrder]);
  const columnIds = useMemo(() => columns.map(c => c.id), [columns]);

  // Group tasks by status with sorting
  const tasksByStatus = useMemo(() => {
    const grouped = groupTasksByStatus(filteredTasks, statuses);
    if (sortSetting !== 'manual') {
      Object.keys(grouped).forEach(statusId => {
        grouped[statusId] = sortTasks(grouped[statusId], sortSetting, customFields);
      });
    }
    return grouped;
  }, [filteredTasks, statuses, sortSetting, customFields]);

  // Task IDs per column for SortableContext
  const taskIdsByColumn = useMemo(() => {
    const result = {};
    columns.forEach(col => { result[col.id] = (tasksByStatus[col.id] || []).map(t => t.id); });
    return result;
  }, [columns, tasksByStatus]);

  // Stats - use decision-aware counting
  // This excludes tasks on non-selected decision branches
  const {
    completedCount,
    totalCount,
    remainingMinutes,
    remainingMinMinutes,
    missingEstCount,
    remainingBreakdown,
  } = useCampaignProgressStats(filteredTasks, statuses, nestedSprints, allSprints);
  
  // Check if any filters are active (including custom field filters)
  const hasActiveFilters = useMemo(() => {
    // Check standard array filters
    const hasArrayFilters = Object.entries(filters).some(([key, value]) => 
      key !== 'customFields' && Array.isArray(value) && value.length > 0
    );
    
    // Check custom field filters
    const hasCustomFieldFilters = filters.customFields && 
      Object.values(filters.customFields).some(criteria => 
        criteria && (
          (criteria.values && criteria.values.length > 0) ||
          (criteria.search && criteria.search.length > 0) ||
          (criteria.operator && criteria.value !== undefined && criteria.value !== '')
        )
      );
    
    return hasArrayFilters || hasCustomFieldFilters;
  }, [filters]);

  // Swimlanes
  const swimlaneContext = useMemo(() => ({
    allTags, nestedSprints, hasPriorityField: false, hasAssigneeField: false, hasDueDateField: false,
  }), [allTags, nestedSprints]);

  const swimlanes = useMemo(() => {
    const grouped = groupTasksIntoSwimlanes(filteredTasks, swimlaneSetting, swimlaneContext);
    if (grouped && sortSetting !== 'manual') {
      grouped.forEach(swimlane => { swimlane.tasks = sortTasks(swimlane.tasks, sortSetting, customFields); });
    }
    return grouped;
  }, [filteredTasks, swimlaneSetting, swimlaneContext, sortSetting, customFields]);

  const hasSwimlanes = swimlanes !== null && swimlanes.length > 0;

  return {
    columns, columnIds, tasksByStatus, taskIdsByColumn,
    completedCount, totalCount, remainingMinutes, remainingMinMinutes, missingEstCount, remainingBreakdown,
    swimlanes, hasSwimlanes,
    collapsedColumns, setCollapsedColumns, expandingColumns, setExpandingColumns,
    hasActiveFilters, filteredTasks,
  };
};

export default useKanbanData;
